import * as Sentry from '@sentry/react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import auth from './api/auth/hooks'
import flags from './api/flags/hooks'
import './assets/scss/themes.scss'
import ErrorModal from './Components/Modals/ErrorModal'
import useOnTabFocus from './hooks/useOnTabFocus'
import Route from './Routes'
import { selectIsAuthenticated } from './store/auth/selectors'

const ErrorFallback = () => {
    const { t } = useTranslation()
    return (
        <div className="d-flex align-items-center justify-content-center">
            <ErrorModal title="Uups!" show={true} onCloseClick={() => window.location.reload()}>
                <p>{t('app.common.errorMessage')}</p>
            </ErrorModal>
        </div>
    )
}

const App = () => {
    const location = useLocation()
    const { t, i18n } = useTranslation()

    const isAuthenticated = useSelector(selectIsAuthenticated)

    const { data: currentAgent } = auth.CurrentAgent.useQuery({
        enabled: isAuthenticated,
        select: (data) => ({
            applicationLanguage: data.applicationLanguage,
            agency: data.agency,
        }),
    })

    flags.GetFeatureFlags.useQuery({ enabled: isAuthenticated, refetchInterval: 60000 })

    // applicationLanguage - user selected lang
    // mainLanguage - agency default lang
    const appLangLocale = currentAgent?.applicationLanguage?.abbreviation
    const mainLangLocale = currentAgent?.agency?.application.mainLanguage?.abbreviation
    const defaultLang = appLangLocale ?? mainLangLocale
    const langLocalStorage = localStorage.getItem('i18nextLng')

    // TODO: App.js's global per-route document.title effect checks i18n key
    // `page.${page}`, but translations actually use `app.common.*` (e.g. app.common.activities) -
    // that key never exists, so the per-page title there always falls back to just the bare app
    // title. Keeping this explicit assignment until that's fixed in its own ticket, otherwise this
    // page loses its title prefix
    useEffect(() => {
        const [, page] = location.pathname.split('/')
        const translation = i18n.exists(`page.${page}`)
        const pageTitle = page && translation ? `${t(`page.${page}`)} |` : ''
        document.title = `${pageTitle} ${t('app.title')}`
    }, [t, location, i18n])

    useEffect(() => {
        if (defaultLang) {
            i18n.changeLanguage(defaultLang)
            document.documentElement.lang = defaultLang
        }
    }, [defaultLang, i18n])

    useOnTabFocus(() => {
        if (defaultLang && defaultLang !== langLocalStorage && isAuthenticated) {
            window.location.reload()
        }
    })

    return (
        <Sentry.ErrorBoundary fallback={ErrorFallback}>
            <Route />
            <ToastContainer closeButton={false} draggable autoClose={3000} />
        </Sentry.ErrorBoundary>
    )
}

export default App

